import { EmailStatusBadge } from "components/EmailStatusBadge";
import { EmailTimeline } from "components/EmailTimeline";
import type { PortalActionMenuItem } from "components/PortalActionMenu";
import { PortalActionMenu } from "components/PortalActionMenu";
import { QuickReplyButton } from "components/QuickReplyButton";
import { StageSuggestionPanel } from "components/StageSuggestionPanel";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { badgeToneDotBg, StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import { usePipelineEmailStatusSummary } from "hooks/use-pipeline-email-api";
import type {
  PipelineFilters,
  SalesPipelineItem,
} from "hooks/use-sales-pipelines-api";
import {
  useCreatePipeline,
  useDeletePipeline,
  usePipelineSummary,
  useSalesPipelines,
  useUpdatePipeline,
  useUpdatePipelineStage,
} from "hooks/use-sales-pipelines-api";
import type React from "react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { DeletePipelineModal } from "routes/crm/SalesBoardPage/components/DeletePipelineModal";
import { PipelineFormModal } from "routes/crm/SalesBoardPage/components/PipelineFormModal";
import {
  formatYenToMan,
  getStageLabel,
  getStageTone,
  PIPELINE_STAGES,
} from "routes/crm/utils";
import { formatDate } from "utils/relative-time";

const STAGES = PIPELINE_STAGES;

/**
 * 日付が過去かどうかを判定する
 * @param {string | null} dateStr 日付文字列
 * @return {boolean} 過去の日付の場合true
 */
function isOverdue(dateStr: string | null): boolean {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

/**
 * ステージ変更時からの経過日数を計算する
 * @param {string | null} stageChangedAt ステージ変更日時
 * @return {number} 経過日数
 */
function calcDaysElapsed(stageChangedAt: string | null): number {
  if (!stageChangedAt) return 0;
  const diff = Date.now() - new Date(stageChangedAt).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

type PipelineActionMenuProps = {
  pipeline: SalesPipelineItem;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (pipeline: SalesPipelineItem) => void;
  onDelete: (pipeline: SalesPipelineItem) => void;
  onStageChange: (pipelineId: string, newStage: string) => void;
};

/**
 * 営業進捗の操作メニュー（Portal ベース）
 * @param {object} props プロパティ
 * @param {SalesPipelineItem} props.pipeline パイプラインデータ
 * @param {boolean} props.canEdit 編集権限
 * @param {boolean} props.canDelete 削除権限
 * @param {Function} props.onEdit 編集コールバック
 * @param {Function} props.onDelete 削除コールバック
 * @param {Function} props.onStageChange ステージ変更コールバック
 * @return {React.ReactElement} 操作メニュー
 */
const PipelineActionMenu: React.FC<PipelineActionMenuProps> = ({
  pipeline,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onStageChange,
}) => {
  const items = useMemo<PortalActionMenuItem[]>(() => {
    const result: PortalActionMenuItem[] = [];
    if (canEdit) {
      result.push({
        key: "edit",
        type: "button",
        label: "編集",
        onClick: () => onEdit(pipeline),
      });
    }
    result.push({ key: "stage-title", type: "title", label: "ステージ変更" });
    for (const stage of STAGES) {
      result.push({
        key: `stage-${stage.value}`,
        type: "button",
        label: (
          <>
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${badgeToneDotBg[stage.tone]}`}
            />
            {stage.label}
          </>
        ),
        onClick: () => onStageChange(pipeline.id, stage.value),
        isActive: pipeline.stage === stage.value,
      });
    }
    if (canDelete) {
      result.push({ key: "divider", type: "divider" });
      result.push({
        key: "delete",
        type: "button",
        label: "削除",
        onClick: () => onDelete(pipeline),
        tone: "destructive",
      });
    }
    return result;
  }, [pipeline, canEdit, canDelete, onEdit, onDelete, onStageChange]);

  return <PortalActionMenu items={items} />;
};

type TalentProgressViewProps = {
  canEdit: boolean;
  canDelete: boolean;
};

/**
 * 人材から見るビューコンポーネント
 * 営業進捗一覧とステージサマリーを表示する
 * @param {object} props プロパティ
 * @param {boolean} props.canEdit 編集権限
 * @param {boolean} props.canDelete 削除権限
 * @return {React.ReactElement} 人材ビュー
 */
export const TalentProgressView: React.FC<TalentProgressViewProps> = ({
  canEdit,
  canDelete,
}) => {
  const [filters, setFilters] = useState<PipelineFilters>({
    page: 1,
    limit: 20,
  });
  const [searchText, setSearchText] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SalesPipelineItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SalesPipelineItem | null>(
    null,
  );

  const { summary, refetch: refetchSummary } = usePipelineSummary();
  const { pipelines, total, loading, error, refetch } =
    useSalesPipelines(filters);
  const { updateStage } = useUpdatePipelineStage();
  const { createPipeline, loading: creating } = useCreatePipeline();
  const { updatePipeline, loading: updating } = useUpdatePipeline();
  const { deletePipeline, loading: deleting } = useDeletePipeline();
  const { summaries: emailSummaries, refetch: refetchEmailSummaries } =
    usePipelineEmailStatusSummary();
  const [expandedPipelineId, setExpandedPipelineId] = useState<string | null>(
    null,
  );

  const handleCreate = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        await createPipeline(data as never);
        toast.success("営業進捗を追加しました");
        setIsCreateModalOpen(false);
        await Promise.all([refetch(), refetchSummary()]);
      } catch {
        toast.error("営業進捗の追加に失敗しました");
      }
    },
    [createPipeline, refetch, refetchSummary],
  );

  const handleEdit = useCallback(
    async (data: Record<string, unknown>) => {
      if (!editTarget) return;
      try {
        await updatePipeline(editTarget.id, data as never);
        toast.success("営業進捗を更新しました");
        setEditTarget(null);
        await Promise.all([refetch(), refetchSummary()]);
      } catch {
        toast.error("営業進捗の更新に失敗しました");
      }
    },
    [editTarget, updatePipeline, refetch, refetchSummary],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deletePipeline(deleteTarget.id);
      toast.success("営業進捗を削除しました");
      setDeleteTarget(null);
      await Promise.all([refetch(), refetchSummary()]);
    } catch {
      toast.error("営業進捗の削除に失敗しました");
    }
  }, [deleteTarget, deletePipeline, refetch, refetchSummary]);

  const handleStageFilterChange = useCallback((stage: string) => {
    setFilters(prev => ({ ...prev, stage: stage || undefined, page: 1 }));
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    [],
  );

  const handleStageChange = useCallback(
    async (pipelineId: string, newStage: string) => {
      try {
        await updateStage(pipelineId, { stage: newStage });
        toast.success("ステージを更新しました");
        await Promise.all([refetch(), refetchSummary()]);
      } catch {
        toast.error("ステージの更新に失敗しました");
      }
    },
    [updateStage, refetch, refetchSummary],
  );

  // Client-side filter by talent name
  const filteredPipelines = searchText
    ? pipelines.filter(p =>
        p.talentName?.toLowerCase().includes(searchText.toLowerCase()),
      )
    : pipelines;

  return (
    <div>
      {/* Summary bar + Add button */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {canEdit && (
          <Button
            type="button"
            size="sm"
            onClick={() => setIsCreateModalOpen(true)}
          >
            営業進捗を追加
          </Button>
        )}
        {STAGES.map(stage => {
          const item = summary.find(s => s.stage === stage.value);
          const count = item?.count ?? 0;
          return (
            <StatusBadge key={stage.value} tone={stage.tone}>
              {stage.label}: {count}
            </StatusBadge>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          className="select select-bordered select-sm"
          value={filters.stage || ""}
          onChange={e => handleStageFilterChange(e.target.value)}
        >
          <option value="">全ステージ</option>
          {STAGES.map(stage => (
            <option key={stage.value} value={stage.value}>
              {stage.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="タレント名で検索..."
          className="input input-bordered input-sm w-64"
          value={searchText}
          onChange={handleSearchChange}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-error mb-4">
          <span>データの取得に失敗しました: {error.message}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <Loading size="lg" />
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <Table.Root density="compact" zebra stickyHeader>
            <Table.Head sticky>
              <tr>
                <Table.HeaderCell>タレント</Table.HeaderCell>
                <Table.HeaderCell>案件</Table.HeaderCell>
                <Table.HeaderCell>ステージ</Table.HeaderCell>
                <Table.HeaderCell>メール</Table.HeaderCell>
                <Table.HeaderCell>提案単価</Table.HeaderCell>
                <Table.HeaderCell>次アクション</Table.HeaderCell>
                <Table.HeaderCell>経過日数</Table.HeaderCell>
                <Table.HeaderCell>担当</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {filteredPipelines.map(pipeline => {
                const overdue = isOverdue(pipeline.nextActionDate);
                const daysElapsed = calcDaysElapsed(pipeline.stageChangedAt);
                const emailSummary = emailSummaries[pipeline.id];
                const isExpanded = expandedPipelineId === pipeline.id;
                return (
                  <Fragment key={pipeline.id}>
                    <Table.Row>
                      <Table.Cell>
                        <Link to={`/crm/talents/${pipeline.talentId}`}>
                          <span className="font-medium">
                            {pipeline.talentName || "-"}
                          </span>
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        {pipeline.focusMatterTitle ||
                          pipeline.matterTitle ||
                          pipeline.clientCompany ||
                          "-"}
                      </Table.Cell>
                      <Table.Cell>
                        <StatusBadge tone={getStageTone(pipeline.stage)}>
                          {getStageLabel(pipeline.stage)}
                        </StatusBadge>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-2 flex-wrap">
                          {emailSummary ? (
                            <EmailStatusBadge
                              status={emailSummary.status}
                              elapsedHours={emailSummary.elapsedHours}
                              lastSubject={emailSummary.lastSubject}
                              unrespondedReplies={
                                emailSummary.unrespondedReplies
                              }
                            />
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                          {canEdit && (
                            <QuickReplyButton
                              pipelineId={pipeline.id}
                              focusMatterId={pipeline.focusMatterId}
                              fallbackSubject={
                                pipeline.focusMatterTitle ||
                                pipeline.matterTitle ||
                                ""
                              }
                              onSent={() => {
                                refetchEmailSummaries();
                              }}
                            />
                          )}
                          <button
                            type="button"
                            className="text-xs text-sky-600 hover:underline"
                            onClick={() =>
                              setExpandedPipelineId(prev =>
                                prev === pipeline.id ? null : pipeline.id,
                              )
                            }
                          >
                            {isExpanded ? "閉じる" : "履歴"}
                          </button>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        {pipeline.proposedReward != null
                          ? formatYenToMan(pipeline.proposedReward)
                          : "-"}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-col">
                          {pipeline.nextActionDate && (
                            <span
                              className={overdue ? "text-error font-bold" : ""}
                            >
                              {formatDate(pipeline.nextActionDate)}
                            </span>
                          )}
                          {pipeline.nextActionMemo && (
                            <span className="text-base-content/60">
                              {pipeline.nextActionMemo}
                            </span>
                          )}
                          {!pipeline.nextActionDate &&
                            !pipeline.nextActionMemo &&
                            "-"}
                        </div>
                      </Table.Cell>
                      <Table.Cell>{daysElapsed}日</Table.Cell>
                      <Table.Cell>{pipeline.assigneeName || "-"}</Table.Cell>
                      <Table.Cell>
                        <PipelineActionMenu
                          pipeline={pipeline}
                          canEdit={canEdit}
                          canDelete={canDelete}
                          onEdit={setEditTarget}
                          onDelete={setDeleteTarget}
                          onStageChange={handleStageChange}
                        />
                      </Table.Cell>
                    </Table.Row>
                    {emailSummary?.status === "has_reply" && (
                      <tr>
                        <td colSpan={9} className="bg-violet-50/40 px-3 py-2">
                          <StageSuggestionPanel
                            pipelineId={pipeline.id}
                            onApplied={() => {
                              refetch();
                              refetchSummary();
                              refetchEmailSummaries();
                            }}
                          />
                        </td>
                      </tr>
                    )}
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} className="bg-slate-50 p-3">
                          <EmailTimeline pipelineId={pipeline.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {filteredPipelines.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="text-center py-8 text-base-content/60"
                  >
                    営業進捗がありません
                  </td>
                </tr>
              )}
            </Table.Body>
          </Table.Root>
        </div>
      )}

      {/* Pagination */}
      {!loading && total > (filters.limit ?? 20) && (
        <div className="flex justify-center mt-4">
          <div className="join">
            <Button
              type="button"
              variant="neutral"
              size="sm"
              isJoinItem
              disabled={(filters.page ?? 1) <= 1}
              onClick={() =>
                setFilters(prev => ({ ...prev, page: (prev.page ?? 1) - 1 }))
              }
            >
              &laquo;
            </Button>
            <Button
              type="button"
              variant="neutral"
              size="sm"
              isJoinItem
              disabled
            >
              {filters.page ?? 1} / {Math.ceil(total / (filters.limit ?? 20))}
            </Button>
            <Button
              type="button"
              variant="neutral"
              size="sm"
              isJoinItem
              disabled={
                (filters.page ?? 1) >= Math.ceil(total / (filters.limit ?? 20))
              }
              onClick={() =>
                setFilters(prev => ({ ...prev, page: (prev.page ?? 1) + 1 }))
              }
            >
              &raquo;
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      <PipelineFormModal
        key={editTarget ? `edit-${editTarget.id}` : "create"}
        isOpen={isCreateModalOpen || !!editTarget}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditTarget(null);
        }}
        onSubmit={editTarget ? handleEdit : handleCreate}
        submitting={editTarget ? updating : creating}
        initialData={editTarget ?? undefined}
      />

      {/* Delete confirm modal */}
      {deleteTarget && (
        <DeletePipelineModal
          pipeline={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
};
